import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AdminPreQualificationDetail } from '../models/preQualificationModel';
import { PreQualificationStatus } from '../enums/PreQualificationEnum';
import { statusConfig } from '../components/PreQualificationStatusBadge';

export interface ResolvedImage {
  url: string;
  dataUrl: string;
  width: number;
  height: number;
}

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 15;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const IMAGE_CELL_SIZE = 32;
const IMAGE_CELL_PADDING = 3;
const LINK_LINE_HEIGHT = 4;
const PRIMARY_COLOR: [number, number, number] = [37, 99, 235];
const HEADER_COLOR: [number, number, number] = [241, 245, 249];
const TEXT_COLOR: [number, number, number] = [30, 41, 59];
const MUTED_COLOR: [number, number, number] = [100, 116, 139];

const getStatusLabel = (status: string): string =>
  statusConfig[status as PreQualificationStatus]?.label || status;

const getFileName = (url: string): string => {
  try {
    const path = new URL(url).pathname;
    return decodeURIComponent(path.split('/').pop() || url);
  } catch {
    return url;
  }
};

// Cell height needed to fit a thumbnail plus its link caption line.
const imageCellHeight = () =>
  IMAGE_CELL_SIZE + IMAGE_CELL_PADDING * 2 + LINK_LINE_HEIGHT;

const buildPreQualificationPdf = (
  pq: AdminPreQualificationDetail,
  images: Map<string, ResolvedImage>,
): jsPDF => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  doc.setTextColor(...TEXT_COLOR);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('Detalle de precalificación', MARGIN, MARGIN + 2);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...MUTED_COLOR);
  doc.text(
    `${pq.borrower.first_name} ${pq.borrower.last_name}`,
    MARGIN,
    MARGIN + 8,
  );
  doc.setTextColor(...TEXT_COLOR);

  // Summary table
  autoTable(doc, {
    startY: MARGIN + 13,
    theme: 'grid',
    margin: { left: MARGIN, right: MARGIN },
    styles: {
      fontSize: 9,
      cellPadding: 3,
      textColor: TEXT_COLOR,
      lineColor: [226, 232, 240],
    },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 45, fillColor: HEADER_COLOR },
    },
    body: [
      ['Nombre', `${pq.borrower.first_name} ${pq.borrower.last_name}`],
      ['Valor solicitado', String(pq.borrower_project.requested_loan_amount)],
      ['Score', pq.borrower.score != null ? String(pq.borrower.score) : 'ND'],
      ['Estado', getStatusLabel(pq.status)],
      ['Fecha de creación', pq.created_at],
      ['Versión de formulario', pq.form_version],
    ],
  });

  let y = (doc as any).lastAutoTable.finalY + 10;

  const ensureSpace = (height: number) => {
    if (y + height > PAGE_HEIGHT - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  };

  pq.questions.forEach((question) => {
    ensureSpace(16);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...PRIMARY_COLOR);
    doc.text(question.title, MARGIN, y);
    doc.setTextColor(...TEXT_COLOR);
    y += 6;

    const visibleResponses = question.responses.filter(
      (response) =>
        response != null &&
        response.label != null &&
        response.value != null &&
        response.value !== '' &&
        !(Array.isArray(response.value) && response.value.length === 0),
    );

    const body = visibleResponses.map((response) => {
      if (Array.isArray(response.value)) {
        // Content rendered manually in didDrawCell; placeholder sizes the row.
        return [response.label, { content: '', styles: {} }];
      }
      if (typeof response.value === 'boolean') {
        return [response.label, response.value ? 'Sí' : 'No'];
      }
      return [response.label, String(response.value)];
    });

    autoTable(doc, {
      startY: y,
      theme: 'striped',
      margin: { left: MARGIN, right: MARGIN },
      rowPageBreak: 'avoid',
      styles: {
        fontSize: 9,
        cellPadding: 3,
        textColor: TEXT_COLOR,
        lineColor: [226, 232, 240],
        valign: 'top',
      },
      showHead: 'never',
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 55 },
        1: { cellWidth: CONTENT_WIDTH - 55 },
      },
      body,
      didParseCell: (data) => {
        if (data.section !== 'body' || data.column.index !== 1) return;
        const response = visibleResponses[data.row.index];
        if (!response || !Array.isArray(response.value)) return;

        const fileUrls = response.value as string[];
        const rowsNeeded = fileUrls.length || 1;
        data.cell.styles.minCellHeight = rowsNeeded * imageCellHeight();
      },
      didDrawCell: (data) => {
        if (data.section !== 'body' || data.column.index !== 1) return;
        const response = visibleResponses[data.row.index];
        if (!response || !Array.isArray(response.value)) return;

        const fileUrls = response.value as string[];
        if (fileUrls.length === 0) {
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(9);
          doc.setTextColor(...MUTED_COLOR);
          doc.text('Sin archivos', data.cell.x + 2, data.cell.y + 6);
          doc.setTextColor(...TEXT_COLOR);
          return;
        }

        fileUrls.forEach((fileUrl, index) => {
          const cellTop = data.cell.y + index * imageCellHeight();
          const resolved = images.get(fileUrl);
          const imageSuffix = fileUrls.length > 1 ? ` (${index + 1})` : '';
          const linkLabel = resolved
            ? `Ver imagen completa${imageSuffix}`
            : getFileName(fileUrl);

          if (resolved) {
            const ratio = Math.min(
              IMAGE_CELL_SIZE / resolved.width,
              IMAGE_CELL_SIZE / resolved.height,
            );
            const w = resolved.width * ratio;
            const h = resolved.height * ratio;
            const imgX = data.cell.x + IMAGE_CELL_PADDING;
            const imgY = cellTop + IMAGE_CELL_PADDING;
            doc.addImage(resolved.dataUrl, imgX, imgY, w, h);
          }

          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(...PRIMARY_COLOR);
          const linkY = cellTop + IMAGE_CELL_SIZE + IMAGE_CELL_PADDING + 2;
          doc.textWithLink(linkLabel, data.cell.x + IMAGE_CELL_PADDING, linkY, {
            url: fileUrl,
          });
          doc.setTextColor(...TEXT_COLOR);
        });
      },
    });

    y = (doc as any).lastAutoTable.finalY + 8;
  });

  return doc;
};

export default buildPreQualificationPdf;
